import { useI18n, useT, type Translate } from "../../../shared/i18n/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Braces,
  CheckCircle2,
  Layers3,
  MoreHorizontal,
  Search,
  Settings2,
} from "lucide-react";
import { useBoards } from "../../../entities/board/model/use-boards";
import { useFolders } from "../../../entities/folder/model/use-folders";
import { useSpaces } from "../../../entities/space/model/use-spaces";
import { useWorkObjectFieldAudit } from "../../../entities/work-object-field/model/use-work-object-field-audit";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import { useWorkObjectFields } from "../../../entities/work-object-field/model/use-work-object-fields";
import { isBoardStatusField } from "../../../entities/work-object/model/work-object-field-drafts";
import {
  getWorkObjectFieldTypeMeta,
  workObjectFieldTypeMeta,
} from "../../../entities/work-object-field/model/work-object-field-type-meta";
import { useWorkObjectTypes } from "../../../entities/work-object-type/model/use-work-object-types";
import {
  WorkObjectFieldDialog,
  type SaveWorkObjectFieldInput,
} from "../../../features/work-object-fields/manage-field/ui/work-object-field-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ContextMenuGroup, ContextMenuItem } from "@/components/ui/context-menu";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type FieldManagerSection = "all" | "workspace" | "type";
type FieldTypeFilter = "all" | WorkObjectFieldDefinition["type"];


export function WorkObjectFieldsPanel() {
  const t = useT();
  const { locale } = useI18n();
  const boards = useBoards();
  const folders = useFolders();
  const fields = useWorkObjectFields();
  const spaces = useSpaces();
  const workObjectTypes = useWorkObjectTypes();
  const [selectedField, setSelectedField] = useState<WorkObjectFieldDefinition | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [section, setSection] = useState<FieldManagerSection>("all");
  const [typeFilter, setTypeFilter] = useState<FieldTypeFilter>("all");
  const audit = useWorkObjectFieldAudit(selectedField?.id ?? null);
  const workspaceId = fields.data[0]?.workspaceId ?? spaces.data[0]?.workspaceId ?? workObjectTypes.data[0]?.workspaceId ?? null;

  // Board status projections are managed from each board's status manager;
  // listing one per board here would drown the real custom fields.
  const manageableFields = useMemo(() => fields.data.filter((field) => !isBoardStatusField(field)), [fields.data]);

  const filteredFields = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return manageableFields.filter((field) => {
      if (section === "workspace" && !isWorkspaceVisible(field)) {
        return false;
      }
      if (section === "type" && field.appliesToAllTypes) {
        return false;
      }
      if (typeFilter !== "all" && field.type !== typeFilter) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }

      return [field.name, field.key, field.type, field.origin].some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [manageableFields, query, section, typeFilter]);

  function openField(field: WorkObjectFieldDefinition | null) {
    setSelectedField(field);
    setIsDialogOpen(true);
  }

  return (
    <div className="grid min-h-0 flex-1 overflow-hidden rounded-lg border border-border/60 bg-card text-card-foreground shadow-sm lg:grid-cols-[230px_minmax(0,1fr)]">
      <aside className="flex flex-col border-r border-border/50 bg-muted/10">
        <div className="px-3 pt-3 pb-2">
          <h2 className="truncate text-base font-semibold">{t("fields.title")}</h2>
          <p className="mt-1 truncate text-sm text-muted-foreground">{t("fields.whatItIs")}</p>
        </div>

        <nav className="flex flex-col gap-1 px-2 pb-2" aria-label={t("fields.sections")}>
          <FieldManagerNavItem
            active={section === "all"}
            count={manageableFields.length}
            icon={Layers3}
            label={t("fields.allFields")}
            onClick={() => setSection("all")}
          />
          <FieldManagerNavItem
            active={section === "workspace"}
            count={manageableFields.filter(isWorkspaceVisible).length}
            icon={Settings2}
            label={t("fields.workspaceFields")}
            onClick={() => setSection("workspace")}
          />
          <FieldManagerNavItem
            active={section === "type"}
            count={manageableFields.filter((field) => !field.appliesToAllTypes).length}
            icon={Braces}
            label={t("fields.typeFields")}
            onClick={() => setSection("type")}
          />
        </nav>
      </aside>

      <section className="flex min-w-0 flex-col">
        <div className="flex flex-col gap-2.5 px-3 py-3">
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">{sectionTitle(section, t)}</h1>
            <p className="mt-1 truncate text-sm text-muted-foreground">{sectionDescription(section, t)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[260px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label={t("fields.search")}
                className="h-8 pl-8"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("fields.searchPlaceholder")}
                value={query}
              />
            </div>
            <Select onValueChange={(value) => setTypeFilter(value as FieldTypeFilter)} value={typeFilter}>
              <SelectTrigger aria-label={t("fields.fieldType")} className="w-[180px]">
                <SelectValue placeholder={t("fields.fieldType")}>
                  {typeFilter === "all" ? t("fields.allTypes") : getWorkObjectFieldTypeMeta(typeFilter, t).label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">{t("fields.allTypes")}</SelectItem>
                  {(Object.keys(workObjectFieldTypeMeta) as Array<WorkObjectFieldDefinition["type"]>).map((type) => (
                    <SelectItem key={type} value={type}>
                      {getWorkObjectFieldTypeMeta(type, t).label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button onClick={() => openField(null)} size="lg">
              {t("fields.newField")}
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {fields.isLoading ? <Skeleton className="h-40 w-full" /> : null}
          {fields.error ? (
            <Alert className="m-4" variant="destructive">
              <AlertDescription>{fields.error}</AlertDescription>
            </Alert>
          ) : null}
          {!fields.isLoading && !fields.error ? (
            filteredFields.length > 0 ? (
              <FieldDefinitionTable fields={filteredFields} onSelect={openField} />
            ) : (
              <Empty className="m-4 min-h-80">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Search />
                  </EmptyMedia>
                  <EmptyTitle>{t("fields.noMatches")}</EmptyTitle>
                  <EmptyDescription>{t("fields.tryAnother")}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            )
          ) : null}
        </div>
      </section>

      <WorkObjectFieldDialog
        auditEvents={audit.data}
        availableBoards={boards.data?.boards ?? []}
        availableFolders={folders.data}
        availableSpaces={spaces.data}
        availableTypes={workObjectTypes.data}
        existingFields={fields.data}
        field={selectedField}
        isArchiving={fields.isArchiving}
        isLoadingAudit={audit.isLoading}
        isSaving={fields.isCreating || fields.isUpdating}
        onArchive={async (fieldId) => {
          await fields.archiveField(fieldId);
          setIsDialogOpen(false);
          setSelectedField(null);
        }}
        onArchiveOption={async (input) => {
          const updated = await fields.archiveFieldOption(input);
          setSelectedField(updated);
          await audit.reload();
          return updated;
        }}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setSelectedField(null);
          }
        }}
        onSave={async (input: SaveWorkObjectFieldInput, field) => {
          if (field) {
            if (field.canRename && (field.name !== input.name.trim() || field.key !== input.key)) {
              await fields.updateField({ id: field.id, key: input.key, name: input.name });
            }
            await fields.updateFieldConfiguration({
              applicableTypeIds: input.applicableTypeIds,
              appliesToAllTypes: input.appliesToAllTypes,
              config: input.config,
              id: field.id,
              locations: input.locations,
              options: input.options,
              relationTargetTypeIds: input.relationTargetTypeIds,
            });
            await audit.reload();
          } else {
            const created = await fields.createField(input);
            await fields.updateFieldConfiguration({
              applicableTypeIds: input.applicableTypeIds,
              appliesToAllTypes: input.appliesToAllTypes,
              config: input.config,
              id: created.id,
              locations: input.locations,
              options: input.options,
              relationTargetTypeIds: input.relationTargetTypeIds,
            });
          }
          setIsDialogOpen(false);
          setSelectedField(null);
        }}
        open={isDialogOpen}
        workspaceId={workspaceId}
      />
    </div>
  );
}

function isWorkspaceVisible(field: WorkObjectFieldDefinition) {
  return field.locations.some((location) => location.scopeType === "workspace");
}

function FieldDefinitionTable({
  fields,
  onSelect,
}: {
  fields: WorkObjectFieldDefinition[];
  onSelect: (field: WorkObjectFieldDefinition) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const columns = useMemo<ColumnDef<WorkObjectFieldDefinition>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        enableSorting: false,
        header: t("fields.name"),
        minSize: 180,
        size: 220,
        meta: {
          locked: true,
        },
        cell: ({ row }) => {
          const field = row.original;
          return <FieldNameCell field={field} />;
        },
      },
      {
        id: "type",
        accessorFn: (field) => getWorkObjectFieldTypeMeta(field.type, t).label,
        header: t("fields.fieldType"),
        minSize: 130,
        size: 160,
        cell: ({ row }) => {
          const meta = getWorkObjectFieldTypeMeta(row.original.type, t);
          const FieldIcon = meta.icon;
          return (
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <FieldIcon className="size-3.5 text-primary" />
              {meta.label}
            </span>
          );
        },
      },
      {
        id: "origin",
        accessorFn: (field) => field.origin,
        header: t("fields.origin"),
        minSize: 110,
        size: 120,
        cell: ({ row }) => <span className="text-muted-foreground">{originLabel(row.original.origin, t)}</span>,
      },
      {
        id: "createdAt",
        accessorFn: (field) => new Date(field.createdAt).getTime(),
        header: t("fields.created"),
        minSize: 120,
        size: 130,
        cell: ({ row }) => (
          <time className="text-muted-foreground">{new Date(row.original.createdAt).toLocaleDateString(locale)}</time>
        ),
      },
      {
        id: "locations",
        accessorFn: (field) => field.locations.length,
        header: t("fields.locations"),
        minSize: 140,
        size: 160,
        cell: ({ row }) => <FieldLocations field={row.original} />,
      },
      {
        id: "actions",
        enableResizing: false,
        enableSorting: false,
        enableHiding: false,
        header: "",
        maxSize: 36,
        minSize: 36,
        size: 36,
        meta: {
          cellClassName: "justify-end px-1",
          headerClassName: "justify-end px-1",
          locked: true,
        },
        cell: ({ row }) => (
          <FieldRowActionsMenu field={row.original} onSelect={onSelect} />
        ),
      },
    ],
    [onSelect],
  );

  return (
    <DataTable
      columns={columns}
      columnOptionsHostColumnId="actions"
      data={fields}
      defaultColumnOrder={["name", "type", "origin", "createdAt", "locations", "actions"]}
      enableColumnVisibility
      fillColumnId="locations"
      fixedColumnIds={["name", "actions"]}
      getRowId={(field) => field.id}
      onRowClick={(row) => onSelect(row.original)}
      renderRowContextMenu={(row) => <FieldRowContextMenu field={row.original} onSelect={onSelect} />}
      stickyColumnIds={["name", "actions"]}
      tableClassName="min-w-[820px]"
    />
  );
}

type FieldRowAction = {
  label: string;
  onAction: () => void;
};

function fieldRowActions(
  field: WorkObjectFieldDefinition,
  onSelect: (field: WorkObjectFieldDefinition) => void,
  t: Translate,
): FieldRowAction[] {
  return [
    {
      label: field.canRename || field.canDelete ? t("common.edit2") : t("common.view"),
      onAction: () => onSelect(field),
    },
  ];
}

function FieldRowActionsMenu({
  field,
  onSelect,
}: {
  field: WorkObjectFieldDefinition;
  onSelect: (field: WorkObjectFieldDefinition) => void;
}) {
  const t = useT();
  const actions = fieldRowActions(field, onSelect, t);

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button aria-label={t("a11y.fieldActions", { name: field.name })} className="size-6" size="icon-xs" variant="ghost">
              {field.canRename || field.canDelete ? <MoreHorizontal /> : <CheckCircle2 />}
            </Button>
          }
        />
        <DropdownMenuContent align="end" className="w-32">
          <DropdownMenuGroup>
            {actions.map((action) => (
              <DropdownMenuItem key={action.label} onClick={action.onAction}>
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function FieldRowContextMenu({
  field,
  onSelect,
}: {
  field: WorkObjectFieldDefinition;
  onSelect: (field: WorkObjectFieldDefinition) => void;
}) {
  const t = useT();
  const actions = fieldRowActions(field, onSelect, t);

  return (
    <ContextMenuGroup>
      {actions.map((action) => (
        <ContextMenuItem key={action.label} onClick={action.onAction}>
          {action.label}
        </ContextMenuItem>
      ))}
    </ContextMenuGroup>
  );
}

function FieldNameCell({ field }: { field: WorkObjectFieldDefinition }) {
  const t = useT();
  const FieldIcon = getWorkObjectFieldTypeMeta(field.type, t).icon;
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const content = (
    <div className="flex max-w-[168px] items-center gap-2">
      <FieldIcon className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate text-xs font-normal" ref={labelRef}>
        {field.name}
      </span>
    </div>
  );

  useEffect(() => {
    const label = labelRef.current;
    if (!label) {
      return undefined;
    }

    const update = () => setIsTruncated(label.scrollWidth > label.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(label);

    return () => observer.disconnect();
  }, [field.name]);

  if (!isTruncated) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={content} />
      <TooltipContent>{field.name}</TooltipContent>
    </Tooltip>
  );
}

function FieldLocations({ field }: { field: WorkObjectFieldDefinition }) {
  const t = useT();
  if (field.locations.length === 0) {
    return <span className="text-muted-foreground">{t("fields.workspace")}</span>;
  }

  const [firstLocation, ...otherLocations] = field.locations;
  if (!firstLocation) {
    return <span className="text-muted-foreground">{t("fields.workspace")}</span>;
  }

  return (
    <span className="inline-flex items-center gap-2 text-muted-foreground">
      <span className="capitalize">{firstLocation.scopeType}</span>
      {otherLocations.length > 0 ? (
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">+{otherLocations.length}</span>
      ) : null}
    </span>
  );
}

function FieldManagerNavItem({
  active,
  count,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  count: number;
  icon: typeof Layers3;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className="flex h-9 items-center gap-2 rounded-lg px-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[active=true]:bg-primary/10 data-[active=true]:text-primary"
      data-active={active}
      onClick={onClick}
      type="button"
    >
      <Icon className="size-4" />
      <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
      <span className="text-xs tabular-nums">{count}</span>
    </button>
  );
}

function sectionTitle(section: FieldManagerSection, t: Translate) {
  if (section === "workspace") return t("fields.workspaceFields");
  if (section === "type") return t("fields.typeFields");
  return t("fields.allTitle");
}

function sectionDescription(section: FieldManagerSection, t: Translate) {
  if (section === "workspace") return t("fields.workspaceDescription");
  if (section === "type") return t("fields.typeDescription");
  return t("fields.allDescription");
}

// "system" / "template" / "custom" are how the record is stored; what a person
// needs to know is whether they may change it, so it is said in words.
function originLabel(origin: string, t: Translate): string {
  if (origin === "system") return t("fields.originSystem");
  if (origin === "default") return t("fields.originDefault");
  if (origin === "template") return t("fields.originTemplate");
  return t("fields.originCustom");
}
